import { EntityStatusInput, RemoveFormItem } from "@/components";
import { FormType, RIGHTS } from "@/enums";
import { selectFilterHelper } from "@/helpers";
import { optionFieldType, optionType, refType } from "@/propTypes";
import { useFormPropertyProvider } from "@/providers";
import { faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Card, Checkbox, Col, Form, Input, Row, Select } from "antd";
import { arrayOf, bool, func } from "prop-types";
import { useIntl } from "react-intl";
import { getFormattedProductCategories, getFormattedRights } from "./helper";
import { OptionEditStatusValueNextStatus } from "./OptionEditStatusValueNextStatus";

export const OptionEditStatusValue = ({
  field,
  remove,
  availableDependantValues,
  labelDisabled,
  isNew,
  formRef,
}) => {
  const intl = useIntl();
  const { getProperty } = useFormPropertyProvider();
  const formattedRights = getFormattedRights(RIGHTS);
  const formattedProductCategories = getFormattedProductCategories(
    getProperty(FormType.Intake, "productCategory")
  );

  const allValues = formRef.current.getFieldValue("values");
  const id = formRef.current.getFieldValue(["values", field.fieldKey, "id"]);
  const filtered = allValues.filter((f) => f.id !== id && f.id);

  return (
    <Card className="even-odd-row">
      <Row>
        <Col flex={1}>
          <Form.Item
            isListField={field.isListField}
            name={[field.name, "label", "fallback"]}
            fieldKey={[field.fieldKey, "label", "fallback"]}
            key={`${field.key}-${field.name}-Label-Fallback`}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "newFormField.optionLabelRequired",
                }),
              },
            ]}
          >
            <Input
              disabled={labelDisabled}
              placeholder={intl.formatMessage({
                id: "newFormField.optionLabelPlaceholder",
              })}
            />
          </Form.Item>
        </Col>
        <Col flex="auto">
          <Form.Item
            isListField={field.isListField}
            name={[field.name, "dependsOnPropertyValues"]}
            fieldKey={[field.fieldKey, "dependsOnPropertyValues"]}
            key={`${field.key}-${field.name}-DependsOnPropertyValues`}
          >
            <Select
              mode="multiple"
              style={{ maxWidth: 500, minWidth: 300 }}
              allowClear
              placeholder={intl.formatMessage({
                id: "newFormField.dependentValuesPlaceholder",
              })}
              filterOption={selectFilterHelper}
            >
              {availableDependantValues.map(({ value, text }) => (
                <Select.Option value={value} key={value}>
                  {text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col flex={1}>
          <EntityStatusInput
            parentComponent="option-edit-status-value"
            field={field}
          />
        </Col>
        {!!isNew && (
          <Col flex={1}>
            <RemoveFormItem
              parentComponent="options-edit-status-value"
              remove={() => remove(field.name)}
            />
          </Col>
        )}
      </Row>
      <Row>
        <Col flex={1}>
          <Form.Item
            isListField={field.isListField}
            label={intl.formatMessage({
              id: "newFormField.defaultPermissionsPlaceholder",
            })}
            name={[field.name, "defaultPermissions"]}
            fieldKey={[field.fieldKey, "defaultPermissions"]}
            key={`${field.key}-${field.name}-DefaultPermissions`}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={intl.formatMessage({
                id: "newFormField.defaultPermissionsPlaceholder",
              })}
              filterOption={selectFilterHelper}
            >
              {formattedRights.map(({ value, label }) => (
                <Select.Option value={value} key={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row key={`${field.key}-checkboxes`}>
        <Col className="ant-form-horizontal" key={`${field.key}-checkboxes-1`}>
          <Form.Item
            isListField={field.isListField}
            name={[field.name, "notifyRequester"]}
            fieldKey={[field.fieldKey, "notifyRequester"]}
            key={`${field.key}-${field.name}-NotifyRequester`}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: "newFormField.notifyRequester",
              })}
            </Checkbox>
          </Form.Item>
        </Col>
        <Col
          style={{ paddingLeft: "10px" }}
          className="ant-form-horizontal"
          key={`${field.key}-checkboxes-2`}
        >
          <Form.Item
            isListField={field.isListField}
            name={[field.name, "notifyAssigned"]}
            fieldKey={[field.fieldKey, "notifyAssigned"]}
            key={`${field.key}-${field.name}-NotifyAssigned`}
            valuePropName="checked"
          >
            <Checkbox>
              {intl.formatMessage({
                id: "newFormField.notifyAssigned",
              })}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col flex="100%">
          <Form.Item
            isListField={field.isListField}
            label={intl.formatMessage({
              id: "newFormField.optionStatusDefaultForProductCategory",
            })}
            name={[field.name, "defaultForProductCategories"]}
            fieldKey={[field.fieldKey, "defaultForProductCategories"]}
            key={`${field.key}-${field.name}-DefaultForProductCategories`}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder={intl.formatMessage({
                id: "newFormField.optionStatusDefaultForProductCategoryPlaceholder",
              })}
              filterOption={selectFilterHelper}
            >
              {formattedProductCategories?.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row key={`next-status-row-${field.key}`}>
        <Card size="small" title="Workflow" style={{ width: "100%" }}>
          <table style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>
                  {intl.formatMessage({
                    id: "newFormField.nextStatus",
                    defaultMessage: "Next Status",
                  })}
                </th>
                <th>
                  {intl.formatMessage({
                    id: "newFormField.nextStatusButtonText",
                    defaultMessage: "Button Text",
                  })}
                </th>
                <th>
                  {intl.formatMessage({
                    id: "newFormField.nextStatusAvailableTo",
                    defaultMessage: "Available To Rights",
                  })}
                </th>
              </tr>
            </thead>

            <Form.List name={[field.name, "nextProjectIntakeStatuses"]}>
              {(fields, { add, remove }) => (
                <>
                  <tbody>
                    {fields.map((nrField) => (
                      <OptionEditStatusValueNextStatus
                        key={`${nrField.fieldKey}-NextStatus`}
                        availableRights={formattedRights}
                        availableStatuses={filtered}
                        field={nrField}
                        remove={remove}
                      />
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      <th colSpan="4">
                        <Form.Item>
                          <Button
                            data-test-id="option-edit-status-value-add-next-status-button"
                            typed="dashed"
                            block
                            icon={<FontAwesomeIcon icon={faPlusCircle} />}
                            onClick={() => {
                              add({});
                            }}
                          >
                            {intl.formatMessage({
                              id: "newFormField.addNextStatus",
                            })}
                          </Button>
                        </Form.Item>
                      </th>
                    </tr>
                  </tfoot>
                </>
              )}
            </Form.List>
          </table>
        </Card>
      </Row>
    </Card>
  );
};

OptionEditStatusValue.propTypes = {
  formRef: refType.isRequired,
  isNew: bool,
  labelDisabled: bool,
  remove: func,
  field: optionFieldType.isRequired,
  availableDependantValues: arrayOf(optionType).isRequired,
};
